import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award, ShieldCheck, Star, TrendingUp } from "lucide-react";

import { api } from "@/lib/api";


/**
 * Phase 17e · Tier progress card
 * Renders progress toward Ambassador (affiliate) or Verified Creator
 * (marketplace) — with active/pending/inactive states.
 */
export function TierProgressCard({ kind }) {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.tierMyStatus()
            .then((s) => { if (!cancelled) setStatus(s); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    if (!status) return null;
    const t = kind === "ambassador" ? status.ambassador : status.verified_creator;
    if (!t) return null;

    const isActive = !!t.active;
    const pending = !!t.revocation_pending;
    const isAmb = kind === "ambassador";

    // progress numbers
    let current, target, unit, label, pct;
    if (isAmb) {
        current = t.current_commission_eur || 0;
        target = t.threshold_eur || 500;
        unit = "$";
        label = "Ambassador · 40% commission";
        pct = target ? Math.min(100, (current / target) * 100) : 0;
    } else {
        // combined progress: published_ratio * paid_ratio
        const pubP = t.published_min ? Math.min(1, (t.published_count || 0) / t.published_min) : 0;
        const paidP = t.paid_min_eur ? Math.min(1, (t.paid_eur || 0) / t.paid_min_eur) : 0;
        pct = ((pubP + paidP) / 2) * 100;
        current = `${t.published_count || 0}/${t.published_min || 3} pub · $${(t.paid_eur || 0).toFixed(2)}/$${t.paid_min_eur || 100}`;
        target = null;
        unit = "";
        label = "Verified Creator · SLA 24h";
    }

    const Icon = isAmb ? Star : ShieldCheck;
    const toneCls = isActive
        ? "text-[var(--color-success)]"
        : pending
        ? "text-neutral-400"
        : "text-[var(--color-secondary)]";

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`tier-card-${kind}`}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 space-y-3"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                    <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
                </div>
                {isActive && !pending && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)] flex items-center gap-1">
                        <Award className="h-3 w-3" /> {isAmb ? "Ambassador" : "Verified"}
                    </span>
                )}
                {pending && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-neutral-400/40 bg-neutral-400/10 text-neutral-400">
                        Revocation pending
                    </span>
                )}
            </div>

            {isAmb ? (
                <div>
                    <div className="flex items-baseline justify-between text-xs">
                        <span className="text-text-secondary">Approved commissions this month</span>
                        <span className="text-text-main tabular-nums">
                            {unit}{current.toFixed(2)}{" "}
                            <span className="text-text-secondary">/ {unit}{target}</span>
                        </span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-[var(--color-bg)] overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${isActive ? "bg-[var(--color-success)]" : "bg-white hover:bg-[#EDEDED]"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <p className="text-[10px] text-text-secondary mt-2">
                        {isActive
                            ? pending
                                ? `You're $${(t.gap_eur || 0).toFixed(2)} below threshold — regain by hitting $${target} this month.`
                                : "Enjoying 40% commission on all NEW conversions."
                            : `Reach $${target}/mo approved commissions to unlock 40% rate.`}
                    </p>
                </div>
            ) : (
                <div>
                    <div className="text-xs text-text-secondary">{current}</div>
                    <div className="mt-2 h-2 rounded-full bg-[var(--color-bg)] overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${isActive ? "bg-[var(--color-success)]" : "bg-white hover:bg-[#EDEDED]"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <p className="text-[10px] text-text-secondary mt-2">
                        {isActive
                            ? pending
                                ? "Metrics dropped below threshold — regain by publishing more."
                                : "Priority moderation lane · SLA 24h · trust badge visible on all your marketplace cards."
                            : `Publish ≥${t.published_min} assets + earn ≥$${t.paid_min_eur} to unlock Verified Creator perks.`}
                    </p>
                </div>
            )}
        </motion.div>
    );
}


export default TierProgressCard;
